import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import AdminProfile from './profiles/AdminProfile';
import EmployeeProfile from './profiles/EmployeeProfile';
import SubContractorProfile from './profiles/SubContractorProfile';
import ClientProfile from './profiles/ClientProfile';
import { PushNotificationSettings } from '../components/PushNotificationSettings';
import { ThemeSettings } from '../components/ThemeSettings';
import { LoginSoundToggle } from '../components/LoginSoundToggle';
import { FamilyInvites } from '../components/FamilyInvites';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Profile = ({ user, onUpdate, onLogout }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [viewingUser, setViewingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // If userId is provided and different from current user, fetch that user's profile
  const isViewingOtherUser = userId && userId !== user.id;
  
  useEffect(() => {
    if (isViewingOtherUser) {
      fetchUserProfile(userId);
    }
  }, [userId, isViewingOtherUser]);
  
  const fetchUserProfile = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/users/${id}`);
      setViewingUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };
  
  // Determine which user to display
  const displayUser = isViewingOtherUser ? viewingUser : user;
  const isViewMode = isViewingOtherUser; // Viewing other users is read-only
  
  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="page-container">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800">{error}</p>
          <Button 
            onClick={() => navigate('/team')} 
            className="mt-4"
            variant="outline"
          >
            Back to Team
          </Button>
        </div>
      </div>
    );
  }
  
  if (!displayUser) {
    return null;
  }
  
  // Add back button if viewing another user
  const BackButton = isViewMode ? (
    <div className="mb-4">
      <Button 
        onClick={() => navigate(-1)} 
        variant="outline"
        className="flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>
    </div>
  ) : null;
  
  // Route to role-specific profile
  const renderProfile = () => {
    switch (displayUser.role) {
      case 'admin':
        return <AdminProfile user={displayUser} onUpdate={onUpdate} onLogout={onLogout} isViewMode={isViewMode} />;
      case 'employee':
        return <EmployeeProfile user={displayUser} onUpdate={onUpdate} onLogout={onLogout} isViewMode={isViewMode} />;
      case 'sub_contractor':
        return <SubContractorProfile user={displayUser} onUpdate={onUpdate} onLogout={onLogout} isViewMode={isViewMode} />;
      case 'client':
        return <ClientProfile user={displayUser} onUpdate={onUpdate} onLogout={onLogout} isViewMode={isViewMode} />;
      default:
        return <EmployeeProfile user={displayUser} onUpdate={onUpdate} onLogout={onLogout} isViewMode={isViewMode} />;
    }
  };

  return (
    <div className="page-container" data-testid="profile-page">
      {BackButton}
      {!isViewMode && <PushNotificationSettings user={user} />}
      {!isViewMode && <ThemeSettings />}
      {!isViewMode && <LoginSoundToggle />}
      {!isViewMode && user.role === 'client' && <FamilyInvites />}
      {renderProfile()}
    </div>
  );
};

export default Profile;
